import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Input, Select, Button, Tag, message, Modal, Form, Radio } from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  MinusOutlined,
  DeleteOutlined,
  PrinterOutlined,
  ShoppingCartOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import {
  POSCartItem,
  POSSale,
  POSPayment,
  PharmacyBatch,
} from "../../interfaces/POS";
import { PharmacyItem } from "../../interfaces/PharmacyItems";
import { Patient } from "../../interfaces/Patient";
import { getPharmacyItems, getPharmacyItemById } from "../../apis/pharmacyItems";
import { getPatients, savePatient } from "../../apis/patient";
import { getUsers } from "../../apis/user";
import { createPOSSale, getPOSSaleById, updatePOSSale } from "../../apis/pos";
import { getPharmacyBillHtml } from "../../apis/receipts";
import { getProductBundles, getProductBundleById } from "../../apis/productBundles";
import { ProductBundle } from "../../interfaces/ProductBundle";
import { useNotification } from "../../NotificationContext";
import { format } from "date-fns";
import DatePicker from "react-datepicker";
import PaymentModal, { PaymentData } from "../../components/PaymentModal";
import "react-datepicker/dist/react-datepicker.css";
import { User } from "../../interfaces/User";
const { Option } = Select;

const PharmacyPOS: React.FC = () => {
  const { saleId } = useParams<{ saleId: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [isEditMode, setIsEditMode] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [loadedVisitId, setLoadedVisitId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [pharmacyItems, setPharmacyItems] = useState<PharmacyItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<PharmacyItem[]>([]);
  const [cart, setCart] = useState<POSCartItem[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [saleDate, setSaleDate] = useState<Date>(new Date());
  const [notes, setNotes] = useState<string>("");
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [discount, setDiscount] = useState<number>(0); // Discount amount (will be converted to percentage)
  const [discountPercentage, setDiscountPercentage] = useState<number>(0); // Order level discount percentage
  const searchInputRef = useRef<any>(null);
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [selectedItemForBatch, setSelectedItemForBatch] =
    useState<PharmacyItem | null>(null);
  const [selectedCartItemForBatch, setSelectedCartItemForBatch] = useState<
    string | null
  >(null);
  const [availableBatches, setAvailableBatches] = useState<PharmacyBatch[]>([]);
  const [selectedBatch, setSelectedBatch] = useState<PharmacyBatch | null>(
    null
  );
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);
  const [quantityToAdd, setQuantityToAdd] = useState(1);
  const [itemSelectKey, setItemSelectKey] = useState(0);
  const [showCreatePatient, setShowCreatePatient] = useState(false);
  const [createPatientLoading, setCreatePatientLoading] = useState(false);
  const [createPatientForm] = Form.useForm();
  const [bundles, setBundles] = useState<ProductBundle[]>([]);
  const [selectedBundleId, setSelectedBundleId] = useState<number | null>(null);
  const [bundleDetail, setBundleDetail] = useState<ProductBundle | null>(null);
  const [bundleDetailLoading, setBundleDetailLoading] = useState(false);
  const [deliveryStatus, setDeliveryStatus] = useState<
    "pending" | "dispatched" | "received" | "completed"
  >("completed");

  // Fetch pharmacy items
  useEffect(() => {
    fetchPharmacyItems();
    fetchPatients();
    fetchUsers();
    fetchBundles();
  }, []);

  // Load existing sale when in edit mode
  useEffect(() => {
    if (!saleId) return;
    const id = parseInt(saleId, 10);
    if (isNaN(id)) return;

    setIsEditMode(true);
    setEditLoading(true);
    getPOSSaleById(id)
      .then((res: any) => {
        const sale = res?.data ?? res;
        if (!sale) {
          showNotification("error", "Sale not found", "", "topRight");
          navigate("/pharmacy/pos/list");
          return;
        }
        if (sale.status === "cancelled") {
          showNotification("error", "Cannot edit cancelled sale", "", "topRight");
          navigate("/pharmacy/pos/list");
          return;
        }

        // Set patient
        const patient = sale.patient;
        if (patient?.id) {
          setSelectedPatient({
            id: patient.id,
            firstName: patient.first_name ?? patient.firstName ?? "",
            lastName: patient.last_name ?? patient.lastName ?? "",
            phone: patient.phone,
            email: patient.email,
          } as Patient);
        }

        // Set doctor/user
        const doctorId = sale.doctor_id ?? sale.doctorId ?? sale.doctor?.id;
        if (doctorId) setSelectedUserId(doctorId);

        // Set sale date
        const dateStr = sale.sale_date ?? sale.saleDate ?? sale.created_at ?? sale.createdAt;
        if (dateStr) setSaleDate(new Date(dateStr));

        // Set notes
        setNotes(sale.notes ?? "");

        // Preserve visit_id for IPD sales
        const vid = sale.visit_id ?? sale.visitId;
        if (vid) setLoadedVisitId(vid);

        // Set order-level discount
        const orderDiscountPct = sale.discount_percentage ?? sale.discountPercentage ?? 0;
        setDiscountPercentage(Number(orderDiscountPct) || 0);

        // Set delivery status (fallback to completed)
        const rawDeliveryStatus =
          sale.delivery_status ?? sale.deliveryStatus;
        const normalizedDeliveryStatus =
          typeof rawDeliveryStatus === "string"
            ? (rawDeliveryStatus.toLowerCase() as
                | "pending"
                | "dispatched"
                | "received"
                | "completed")
            : null;
        if (
          normalizedDeliveryStatus === "pending" ||
          normalizedDeliveryStatus === "dispatched" ||
          normalizedDeliveryStatus === "received" ||
          normalizedDeliveryStatus === "completed"
        ) {
          setDeliveryStatus(normalizedDeliveryStatus);
        } else {
          setDeliveryStatus("completed");
        }

        // Convert sale items to cart format
        const items = sale.items ?? sale.sale_items ?? [];
        const cartItems: POSCartItem[] = items.map((item: any, idx: number) => {
          const pharmacyItem = item.pharmacy_item ?? item.pharmacyItem;
          const batch = item.batch;
          const itemId = pharmacyItem?.id ?? item.pharmacy_item_id ?? item.pharmacyItemId;
          const qty = Number(item.quantity ?? 1);
          const mrp = Number(item.mrp ?? item.selling_price ?? item.sellingPrice ?? 0);
          const unitPrice = Number(item.selling_price ?? item.sellingPrice ?? mrp ?? 0);
          const taxPct = Number(item.tax_percentage ?? item.taxPercentage ?? batch?.tax_percentage ?? 0);
          const discountAmount = Number(item.discount_amount ?? item.discountAmount ?? 0);
          const gross = qty * mrp;
          const discountPct = gross > 0 ? (discountAmount / gross) * 100 : 0;
          const netAmount = Number(item.net_amount ?? item.netAmount ?? item.total ?? 0);

          return {
            id: `item-edit-${item.id ?? idx}-${Date.now()}`,
            saleItemId: item.id,
            pharmacyItemId: itemId,
            name: pharmacyItem?.name ?? item.name ?? "-",
            sku: pharmacyItem?.sku ?? item.sku ?? "",
            barcode: pharmacyItem?.barcode ?? item.barcode,
            quantity: qty,
            unitPrice,
            mrp,
            discount: discountAmount,
            discountPercentage: discountPct,
            tax: Number(item.tax_amount ?? item.taxAmount ?? 0),
            taxPercentage: taxPct,
            total: netAmount,
            form: pharmacyItem?.form ?? item.form,
            strength: pharmacyItem?.strength ?? item.strength,
            unit: pharmacyItem?.unit ?? item.unit,
            batchId: batch?.id ?? item.pharmacy_batch_id ?? item.pharmacyBatchId,
            batchNumber: batch?.batch_number ?? batch?.batchNumber ?? item.batch_number,
            expiryDate: batch?.expiry_date ?? batch?.expiryDate ?? item.expiry_date,
          };
        });
        setCart(cartItems);
      })
      .catch((err) => {
        console.error("Error loading sale:", err);
        showNotification("error", "Failed to load sale", err?.message ?? "", "topRight");
        navigate("/pharmacy/pos/list");
      })
      .finally(() => setEditLoading(false));
  }, [saleId, navigate, showNotification]);

  const fetchBundles = async () => {
    try {
      const res: any = await getProductBundles();
      const data = Array.isArray(res) ? res : res?.data ?? [];
      setBundles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch bundles:", err);
    }
  };

  useEffect(() => {
    if (!selectedBundleId) {
      setBundleDetail(null);
      return;
    }
    setBundleDetailLoading(true);
    getProductBundleById(selectedBundleId)
      .then(async (res: any) => {
        const b = res?.data ?? res ?? null;
        if (!b?.items?.length) {
          setSelectedBundleId(null);
          setBundleDetail(null);
          return;
        }
        // Resolve pharmacy items with batches: fetch full item with batches for every bundle item to ensure we have batch data
        const itemsWithBatches: PharmacyItem[] = [...pharmacyItems];
        for (const bi of b.items) {
          const itemId = bi.pharmacy_item_id ?? bi.pharmacyItemId ?? bi.pharmacy_item?.id ?? bi.pharmacyItem?.id;
          if (!itemId) continue;
          const fromList = pharmacyItems.find((p) => p.id === itemId);
          const fromBundle = bi.pharmacy_item ?? bi.pharmacyItem;
          const hasBatches = (batches: any[]) => Array.isArray(batches) && batches.length > 0;
          const listHasBatches = fromList?.batches && hasBatches(fromList.batches);
          const bundleHasBatches = fromBundle?.batches && hasBatches(fromBundle.batches);
          // Always fetch full item when we don't have usable batches (ensures batch_id is never null for batch-tracked items)
          if (!listHasBatches && !bundleHasBatches) {
            try {
              const fetched: any = await getPharmacyItemById(itemId);
              const fullItem = fetched?.data ?? fetched;
              const batches = fullItem?.batches ?? [];
              if (fullItem && (fullItem.id ?? fullItem.pharmacy_item_id)) {
                const resolved = { ...(fromList || fromBundle || {}), ...fullItem, id: fullItem.id ?? fullItem.pharmacy_item_id ?? itemId, batches };
                const idx = itemsWithBatches.findIndex((p) => (p.id ?? (p as any).pharmacy_item_id) === itemId);
                if (idx >= 0) itemsWithBatches[idx] = resolved;
                else itemsWithBatches.push(resolved);
              }
            } catch (_) {
              // Merge bundle item data as fallback
              if (fromBundle) {
                const idx = itemsWithBatches.findIndex((p) => (p.id ?? (p as any).pharmacy_item_id) === itemId);
                const merged = { ...(fromList || {}), ...fromBundle, id: itemId };
                if (idx >= 0) itemsWithBatches[idx] = merged;
                else itemsWithBatches.push(merged);
              }
            }
          } else if (fromBundle?.batches && !listHasBatches) {
            // Use bundle's pharmacy_item with batches
            const merged = { ...(fromList || {}), ...fromBundle, id: itemId };
            const idx = itemsWithBatches.findIndex((p) => (p.id ?? (p as any).pharmacy_item_id) === itemId);
            if (idx >= 0) itemsWithBatches[idx] = merged;
            else itemsWithBatches.push(merged);
          }
        }
        const newCartItems = buildCartItemsFromBundle(b, itemsWithBatches);
        if (newCartItems.length > 0) {
          setCart((prev) => [...prev, ...newCartItems]);
        }
        setSelectedBundleId(null);
        setBundleDetail(null);
      })
      .catch((err: any) => {
        console.error("Failed to fetch bundle detail:", err);
        setBundleDetail(null);
        showNotification("error", "Failed to load bundle", "", "topRight");
      })
      .finally(() => setBundleDetailLoading(false));
  }, [selectedBundleId, pharmacyItems]);

  // Filter items based on search query
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredItems([]);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = pharmacyItems.filter(
      (item) =>
        (item.name ?? "").toLowerCase().includes(query) ||
        (item.sku ?? "").toLowerCase().includes(query) ||
        (item.barcode ?? "").toLowerCase().includes(query) ||
        (item.molecule ?? "").toLowerCase().includes(query)
    );
    setFilteredItems(filtered.slice(0, 10)); // Limit to 10 results
  }, [searchQuery, pharmacyItems]);

  const fetchPharmacyItems = async () => {
    try {
      const response: any = await getPharmacyItems({
        per_page: 1000,
        is_active: true,
      });
      // Handle the response structure: { data: [...], links: {...}, meta: {...} }
      const items = Array.isArray(response) ? response : response?.data || [];
      setPharmacyItems(items || []);
    } catch (error) {
      console.error("Error fetching pharmacy items:", error);
      showNotification(
        "error",
        "Failed to load pharmacy items",
        "",
        "topRight"
      );
    }
  };

  const fetchPatients = async () => {
    try {
      const response: any = await getPatients({ per_page: 100 });
      setPatients(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching patients:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response: any = await getUsers({ per_page: 100 });
      setUsers(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    const userStr = localStorage.getItem("user");

    if (!userStr || users.length === 0) return;

    try {
      const loggedInUser: User = JSON.parse(userStr);

      const matchedUser = users.find(
        (u: User) => u.id === loggedInUser.id
      );

      if (matchedUser && matchedUser.id) {
        setSelectedUserId(matchedUser.id);
      }
    } catch (err) {
      console.error("Invalid user object in localStorage");
    }
  }, [users]);

  // Get default unit price from item (first usable batch or 0) for non-batch-tracked add
  // Get tax percentage from batch (API: batch.tax_percentage or batch.grn_item.tax_percentage)
  const getTaxPercentageFromBatch = (batch: any): number => {
    if (!batch) return 0;
    const raw = batch.tax_percentage ?? batch.taxPercentage ?? batch.grn_item?.tax_percentage ?? batch.grn_item?.taxPercentage;
    const n = typeof raw === "string" ? parseFloat(raw) : Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  // Reverse-calculate taxable from inclusive MRP: taxable = inclusive_price / (1 + gst%)
  const getTaxableFromMrp = (mrp: number, taxPercentage: number): number => {
    if (!taxPercentage || !Number.isFinite(taxPercentage)) return mrp;
    const divisor = 1 + taxPercentage / 100;
    const taxable = mrp / divisor;
    return Number.isFinite(taxable) ? taxable : mrp;
  };

  const getDefaultUnitPrice = (item: PharmacyItem): number => {
    const raw = item?.batches || [];
    const usable = raw
      .map((batch: any) => ({
        selling_price: batch.sellingPrice ?? batch.selling_price ?? "0",
        available_quantity: batch.availableQuantity ?? batch.available_quantity ?? 0,
        is_active: batch.isActive ?? batch.is_active ?? true,
        is_expired: batch.isExpired ?? batch.is_expired ?? false,
      }))
      .filter(
        (b: any) =>
          b.available_quantity > 0 && b.is_active && !b.is_expired
      );
    if (usable.length === 0) return 0;
    return parseFloat(usable[0].selling_price || "0") || 0;
  };

  // Add item to cart without batch (when is_batch_tracked is false)
  const addToCartWithoutBatch = (item: PharmacyItem, quantity: number = 1) => {
    const firstBatch = (item.batches || [])[0];
    const sellingPrice = getDefaultUnitPrice(item);
    const mrp = (firstBatch as any)?.mrp ?? sellingPrice;
    const taxPct = getTaxPercentageFromBatch(firstBatch);
    const unitPrice = getTaxableFromMrp(mrp, taxPct);
    const qty = quantity >= 1 ? quantity : 1;
    const newCartItem: POSCartItem = {
      id: `item-${Date.now()}-${item.id}`,
      pharmacyItemId: item.id!,
      name: item.name,
      sku: item.sku,
      barcode: item.barcode,
      quantity: qty,
      unitPrice,
      mrp,
      discount: 0,
      discountPercentage: 0,
      tax: 0,
      taxPercentage: taxPct,
      total: (unitPrice * qty) * (1 - 0 / 100) * (1 + (taxPct || 0) / 100),
      form: item.form,
      strength: item.strength,
      unit: item.unit,
    };
    setCart([...cart, newCartItem]);
    setSearchQuery("");
    setFilteredItems([]);
    if (searchInputRef.current) searchInputRef.current.focus();
  };

  // Check if item has at least one usable batch (same logic as fetchBatchesForItem)
  const hasUsableBatches = (item: PharmacyItem): boolean => {
    const raw = item?.batches || [];
    if (raw.length === 0) return false;
    const usable = raw
      .map((batch: any) => ({
        available_quantity: batch.availableQuantity ?? batch.available_quantity ?? 0,
        is_active: batch.isActive ?? batch.is_active ?? true,
        is_expired: batch.isExpired ?? batch.is_expired ?? false,
      }))
      .filter(
        (b: any) =>
          b.available_quantity > 0 && b.is_active && !b.is_expired
      );
    return usable.length > 0;
  };

  // Get batches from pharmacy item instead of making API call
  const fetchBatchesForItem = (item: PharmacyItem) => {
    if (!item?.id) return;

    setIsLoadingBatches(true);

    try {
      // Get batches from the pharmacy item's batches array
      const batches = (item.batches || []).map((batch: any) => {
        // Map the batch structure to match PharmacyBatch interface
        // API returns camelCase, convert to snake_case for PharmacyBatch interface
        return {
          id: batch.id,
          batch_number: batch.batchNumber ?? batch.batch_number,
          manufacturing_date: batch.manufacturingDate ?? batch.manufacturing_date,
          expiry_date: batch.expiryDate ?? batch.expiry_date,
          purchase_price: (batch.purchasePrice ?? batch.purchase_price)?.toString() || "0",
          selling_price: (batch.sellingPrice ?? batch.selling_price)?.toString() || "0",
          available_quantity: batch.availableQuantity ?? batch.available_quantity ?? 0,
          is_expired: batch.isExpired ?? batch.is_expired ?? false,
          is_expiring_soon: batch.isExpiringSoon ?? batch.is_expiring_soon ?? false,
          supplier_id: batch.supplierId ?? batch.supplier_id,
          notes: batch.notes,
          is_active: batch.isActive ?? batch.is_active ?? true,
          mrp: parseFloat(batch.sellingPrice || batch.selling_price || batch.purchasePrice || batch.purchase_price || "0"),
          tax_percentage: batch.tax_percentage ?? batch.taxPercentage ?? batch.grn_item?.tax_percentage ?? batch.grn_item?.taxPercentage,
        };
      }).filter((batch: any) => batch.available_quantity > 0 && batch.is_active && !batch.is_expired);

      setAvailableBatches(batches);

      // Auto-select if only one usable batch
      if (batches.length === 1) {
        setSelectedBatch(batches[0]);
      } else {
        setSelectedBatch(null);
      }
    } catch (error) {
      console.error("Error processing batches:", error);
      setAvailableBatches([]);
      setSelectedBatch(null);
    } finally {
      setIsLoadingBatches(false);
    }
  };

  const getItemDiscountPercentageFromBatch = (batch: any): number => {
    console.log({ batch });

    if (!batch) return 0;
    const raw = batch.discount_percentage ?? batch.discountPercentage ?? batch.grnItem?.discountPercentage ?? batch.grnItem?.discount_percentage;
    const n = typeof raw === "string" ? parseFloat(raw) : Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  const calculateItemTotal = (item: POSCartItem): number => {
    const itemMrp = item.mrp ?? item.unitPrice * (1 + (item.taxPercentage || 0) / 100);
    const qty = item.quantity || 0;
    const taxPct = item.taxPercentage || 0;
    const discountPct = item.discountPercentage || 0;
    const gross = qty * itemMrp; // MRP total (tax-inclusive)
    const discountAmount = gross * (discountPct / 100); // discount on MRP
    const grossAfterDiscount = Math.max(0, gross - discountAmount);
    if (!taxPct) return grossAfterDiscount;
    const taxableAfterDiscount = grossAfterDiscount / (1 + taxPct / 100);
    const taxAfterDiscount = grossAfterDiscount - taxableAfterDiscount;
    return taxableAfterDiscount + taxAfterDiscount;
  };

  const handleItemSelect = (item: PharmacyItem) => {
    // Always open the batch modal so the user can choose which batch to use.
    // If the item is already in the cart with a different batch, this lets them
    // add a second line for the other batch instead of silently bumping the
    // first-batch quantity beyond what's available.
    setQuantityToAdd(1);
    setSelectedItemForBatch(item);
    fetchBatchesForItem(item);
    setIsBatchModalOpen(true);
  };

  const addToCartWithBatch = () => {
    if (!selectedItemForBatch) return;

    const mrp = selectedBatch
      ? (selectedBatch.mrp ?? parseFloat(selectedBatch.selling_price || "0"))
      : getDefaultUnitPrice(selectedItemForBatch);
    const taxPct = getTaxPercentageFromBatch(selectedBatch ?? null);
    const unitPrice = getTaxableFromMrp(mrp, taxPct);

    // If updating an existing cart item
    if (selectedCartItemForBatch) {
      setCart(
        cart.map((cartItem) => {
          if (cartItem.id === selectedCartItemForBatch) {
            const updatedItem: POSCartItem = {
              ...cartItem,
              batchId: selectedBatch?.id,
              batchNumber: selectedBatch?.batch_number,
              expiryDate: selectedBatch?.expiry_date,
              unitPrice,
              mrp,
              taxPercentage: taxPct,
              discountPercentage: getItemDiscountPercentageFromBatch(selectedBatch ?? undefined),
            };
            return {
              ...updatedItem,
              total: calculateItemTotal(updatedItem),
            };
          }
          return cartItem;
        })
      );
    } else {
      const qty = quantityToAdd >= 1 ? quantityToAdd : 1;

      // If a line for this exact item+batch already exists, increment it rather
      // than adding a duplicate row. This covers re-selecting the same item from
      // the search after it's already in the cart.
      const existingLine = selectedBatch
        ? cart.find(
            (c) =>
              c.pharmacyItemId === selectedItemForBatch.id! &&
              c.batchId === selectedBatch.id
          )
        : null;

      if (existingLine) {
        setCart(
          cart.map((c) => {
            if (c.id !== existingLine.id) return c;
            const updatedItem = { ...c, quantity: c.quantity + qty };
            return { ...updatedItem, total: calculateItemTotal(updatedItem) };
          })
        );
      } else {
        const newCartItem: POSCartItem = {
          id: `item-${Date.now()}-${selectedItemForBatch.id}`,
          pharmacyItemId: selectedItemForBatch.id!,
          name: selectedItemForBatch.name,
          sku: selectedItemForBatch.sku,
          barcode: selectedItemForBatch.barcode,
          quantity: qty,
          unitPrice,
          mrp,
          discount: 0,
          discountPercentage: getItemDiscountPercentageFromBatch(selectedBatch ?? undefined),
          tax: 0,
          taxPercentage: taxPct,
          total: 0,
          form: selectedItemForBatch.form,
          strength: selectedItemForBatch.strength,
          unit: selectedItemForBatch.unit,
          batchId: selectedBatch?.id,
          batchNumber: selectedBatch?.batch_number,
          expiryDate: selectedBatch?.expiry_date,
        };
        newCartItem.total = calculateItemTotal(newCartItem);
        setCart([...cart, newCartItem]);
      }
    }

    // Reset batch selection
    setIsBatchModalOpen(false);
    setSelectedItemForBatch(null);
    setSelectedCartItemForBatch(null);
    setSelectedBatch(null);
    setAvailableBatches([]);
    setQuantityToAdd(1);
    setSearchQuery("");
    setFilteredItems([]);

    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Build cart items from a bundle; use pharmacyItems from state for full item data (including batches)
  const buildCartItemsFromBundle = (bundle: ProductBundle, pharmacyItemsList: PharmacyItem[]): POSCartItem[] => {
    const newItems: POSCartItem[] = [];
    const baseId = Date.now();
    (bundle.items ?? []).forEach((bi: any, idx: number) => {
      const itemId = bi.pharmacy_item_id ?? bi.pharmacyItemId ?? bi.pharmacy_item?.id ?? bi.pharmacyItem?.id;
      const pharmacyItem = pharmacyItemsList.find((p) => p.id === itemId) ?? bi.pharmacy_item ?? bi.pharmacyItem;
      const qty = Math.max(1, bi.quantity ?? bi.sort_order ?? 1);
      if (!pharmacyItem?.id) return;

      const isBatchTracked = pharmacyItem.is_batch_tracked ?? pharmacyItem.isBatchTracked ?? false;
      if (isBatchTracked) {
        const batch = getBatchWithNearestExpiry(pharmacyItem);
        if (!batch) {
          message.warning(`No usable batch for ${pharmacyItem.name ?? "item"}. Skipping from bundle.`);
          return;
        }
        const mrp = batch.mrp ?? parseFloat(batch.selling_price || "0");
        const taxPct = getTaxPercentageFromBatch(batch);
        const unitPrice = getTaxableFromMrp(mrp, taxPct);
        const discountPct = getItemDiscountPercentageFromBatch(batch);
        const cartItem: POSCartItem = {
          id: `item-${baseId}-${pharmacyItem.id}-${batch.id}-${idx}`,
          pharmacyItemId: pharmacyItem.id,
          name: pharmacyItem.name,
          sku: pharmacyItem.sku ?? "",
          barcode: pharmacyItem.barcode,
          quantity: qty,
          unitPrice,
          mrp,
          discount: 0,
          discountPercentage: discountPct,
          tax: 0,
          taxPercentage: taxPct,
          total: 0,
          form: pharmacyItem.form,
          strength: pharmacyItem.strength,
          unit: pharmacyItem.unit,
          batchId: batch.id,
          batchNumber: batch.batch_number,
          expiryDate: batch.expiry_date,
        };
        cartItem.total = calculateItemTotal(cartItem);
        newItems.push(cartItem);
      } else {
        // Non-batch-tracked: still try to use latest-expiring batch when available (backend may require batch_id)
        const batch = getBatchWithNearestExpiry(pharmacyItem);
        const sellingPrice = getDefaultUnitPrice(pharmacyItem);
        const firstBatch = (pharmacyItem.batches || [])[0];
        const mrp = batch ? (batch.mrp ?? parseFloat(batch.selling_price || "0")) : (firstBatch as any)?.mrp ?? sellingPrice;
        const taxPct = getTaxPercentageFromBatch(batch ?? firstBatch);
        const unitPrice = getTaxableFromMrp(mrp, taxPct);
        const lineTotal = (unitPrice * qty) * (1 + (taxPct || 0) / 100);
        const cartItem: POSCartItem = {
          id: `item-${baseId}-${pharmacyItem.id}-${batch?.id ?? idx}-${idx}`,
          pharmacyItemId: pharmacyItem.id,
          name: pharmacyItem.name,
          sku: pharmacyItem.sku ?? "",
          barcode: pharmacyItem.barcode,
          quantity: qty,
          unitPrice,
          mrp,
          discount: 0,
          discountPercentage: 0,
          tax: 0,
          taxPercentage: taxPct,
          total: lineTotal,
          form: pharmacyItem.form,
          strength: pharmacyItem.strength,
          unit: pharmacyItem.unit,
        };
        if (batch) {
          cartItem.batchId = batch.id;
          cartItem.batchNumber = batch.batch_number;
          cartItem.expiryDate = batch.expiry_date;
        }
        newItems.push(cartItem);
      }
    });
    return newItems;
  };

  // Get batch with nearest expiry (FEFO) from item's batches for bundle flow
  const getBatchWithNearestExpiry = (item: PharmacyItem): PharmacyBatch | null => {
    const raw = item?.batches || [];
    const usable = raw
      .map((batch: any) => {
        const availQty = batch.availableQuantity ?? batch.available_quantity ?? batch.current_quantity ?? batch.currentQuantity ?? 0;
        return {
          id: batch.id ?? batch.pharmacy_batch_id,
          batch_number: batch.batchNumber ?? batch.batch_number,
          manufacturing_date: batch.manufacturingDate ?? batch.manufacturing_date,
          expiry_date: batch.expiryDate ?? batch.expiry_date,
          purchase_price: batch.purchasePrice?.toString() ?? batch.purchase_price ?? "0",
          selling_price: batch.sellingPrice?.toString() ?? batch.selling_price ?? "0",
          available_quantity: availQty,
          is_expired: batch.isExpired ?? batch.is_expired ?? false,
          is_expiring_soon: batch.isExpiringSoon ?? batch.is_expiring_soon,
          supplier_id: batch.supplierId ?? batch.supplier_id,
          notes: batch.notes,
          is_active: batch.isActive ?? batch.is_active ?? true,
          mrp: parseFloat(batch.sellingPrice ?? batch.selling_price ?? batch.purchasePrice ?? batch.purchase_price ?? "0"),
          tax_percentage: batch.tax_percentage ?? batch.taxPercentage ?? batch.grn_item?.tax_percentage ?? batch.grn_item?.taxPercentage,
          grn_item: batch.grn_item,
        };
      })
      .filter((b: any) => (b.available_quantity ?? 1) > 0 && b.is_active && !b.is_expired);
    if (usable.length === 0) return null;
    usable.sort((a: any, b: any) => {
      const dateA = a.expiry_date ? new Date(a.expiry_date).getTime() : Number.MAX_SAFE_INTEGER;
      const dateB = b.expiry_date ? new Date(b.expiry_date).getTime() : Number.MAX_SAFE_INTEGER;
      return dateA - dateB;
    });
    return usable[0] as PharmacyBatch;
  };

  // Add bundle item to cart: use nearest-expiry batch when batch-tracked, else add without batch
  const addBundleItemToCart = (pharmacyItem: PharmacyItem, quantity: number) => {
    const qty = quantity >= 1 ? quantity : 1;
    if (pharmacyItem.is_batch_tracked) {
      const batch = getBatchWithNearestExpiry(pharmacyItem);
      if (!batch) {
        message.warning(`No usable batch for ${pharmacyItem.name}. Skipping.`);
        return;
      }
      const mrp = batch.mrp ?? parseFloat(batch.selling_price || "0");
      const taxPct = getTaxPercentageFromBatch(batch);
      const unitPrice = getTaxableFromMrp(mrp, taxPct);
      const newCartItem: POSCartItem = {
        id: `item-${Date.now()}-${pharmacyItem.id}-${batch.id}`,
        pharmacyItemId: pharmacyItem.id!,
        name: pharmacyItem.name,
        sku: pharmacyItem.sku,
        barcode: pharmacyItem.barcode,
        quantity: qty,
        unitPrice,
        mrp,
        discount: 0,
        discountPercentage: getItemDiscountPercentageFromBatch(batch),
        tax: 0,
        taxPercentage: taxPct,
        total: 0,
        form: pharmacyItem.form,
        strength: pharmacyItem.strength,
        unit: pharmacyItem.unit,
        batchId: batch.id,
        batchNumber: batch.batch_number,
        expiryDate: batch.expiry_date,
      };
      newCartItem.total = calculateItemTotal(newCartItem);
      setCart([...cart, newCartItem]);
    } else {
      addToCartWithoutBatch(pharmacyItem, qty);
    }
    setSearchQuery("");
    setFilteredItems([]);
    if (searchInputRef.current) searchInputRef.current.focus();
  };

  const openBatchModalForBundleItem = (pharmacyItem: PharmacyItem, quantity: number) => {
    setSelectedItemForBatch(pharmacyItem);
    setSelectedCartItemForBatch(null);
    setQuantityToAdd(quantity >= 1 ? quantity : 1);
    fetchBatchesForItem(pharmacyItem);
    setIsBatchModalOpen(true);
  };

  const addToCart = (item: PharmacyItem) => {
    handleItemSelect(item);
  };

  const updateCartItemQuantity = (itemId: string, change: number) => {
    setCart(
      cart.map((item) => {
        if (item.id === itemId) {
          const newQuantity = Math.max(1, item.quantity + change);
          const updatedItem = {
            ...item,
            quantity: newQuantity,
          };
          return {
            ...updatedItem,
            total: calculateItemTotal(updatedItem),
          };
        }
        return item;
      })
    );
  };

  const updateCartItemPrice = (itemId: string, price: number) => {
    setCart(
      cart.map((item) => {
        if (item.id === itemId) {
          const updatedItem = {
            ...item,
            unitPrice: price,
          };
          return {
            ...updatedItem,
            total: calculateItemTotal(updatedItem),
          };
        }
        return item;
      })
    );
  };

  const updateCartItemDiscountPercentage = (itemId: string, discountPct: number) => {
    const clamped = Math.min(100, Math.max(0, discountPct));
    setCart(
      cart.map((item) => {
        if (item.id === itemId) {
          const updatedItem = {
            ...item,
            discountPercentage: clamped,
          };
          return {
            ...updatedItem,
            total: calculateItemTotal(updatedItem),
          };
        }
        return item;
      })
    );
  };

  // When tax % changes: recalc taxable from MRP (taxable = mrp / (1 + gst%)) and update unitPrice
  const updateCartItemTaxPercentage = (itemId: string, taxPct: number) => {
    const clamped = Math.min(100, Math.max(0, taxPct));
    setCart(
      cart.map((item) => {
        if (item.id !== itemId) return item;
        const mrp = item.mrp ?? item.unitPrice * (1 + (item.taxPercentage || 0) / 100);
        const newUnitPrice = getTaxableFromMrp(mrp, clamped);
        const updatedItem: POSCartItem = {
          ...item,
          taxPercentage: clamped,
          unitPrice: newUnitPrice,
        };
        return { ...updatedItem, total: calculateItemTotal(updatedItem) };
      })
    );
  };

  const removeFromCart = (itemId: string) => {
    setCart(cart.filter((item) => item.id !== itemId));
  };

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => {
      const itemMrp = item.mrp ?? item.unitPrice * (1 + (item.taxPercentage || 0) / 100);
      return sum + item.quantity * itemMrp;
    }, 0);
  };

  const calculateTotalDiscount = () => {
    // Calculate item-level discounts
    const itemDiscounts = cart.reduce((sum, item) => {
      const itemMrp = item.mrp ?? item.unitPrice * (1 + (item.taxPercentage || 0) / 100);
      return sum + item.quantity * itemMrp * ((item.discountPercentage || 0) / 100);
    }, 0);

    // Add order-level discount
    const subtotal = calculateSubtotal();
    const orderDiscount = subtotal * (discountPercentage / 100);

    return itemDiscounts + orderDiscount;
  };

  const calculateTotalTax = () => {
    return cart.reduce((sum, item) => {
      const itemMrp = item.mrp ?? item.unitPrice * (1 + (item.taxPercentage || 0) / 100);
      const gross = item.quantity * itemMrp;
      const itemDiscount = gross * ((item.discountPercentage || 0) / 100);
      const grossAfterDiscount = Math.max(0, gross - itemDiscount);
      const taxPct = item.taxPercentage || 0;
      const itemTax = taxPct ? (grossAfterDiscount - grossAfterDiscount / (1 + taxPct / 100)) : 0;
      return sum + itemTax;
    }, 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const totalDiscount = calculateTotalDiscount();
    return Math.max(0, subtotal - totalDiscount);
  };

  const handleCreatePatient = async () => {
    try {
      const values = await createPatientForm.validateFields();
      setCreatePatientLoading(true);
      const res: any = await savePatient(values);
      const created = res?.data ?? res;
      const newPatient: Patient = {
        id: created.id,
        firstName: created.firstName ?? created.first_name ?? values.firstName,
        lastName: created.lastName ?? created.last_name ?? values.lastName,
        phone: created.phone ?? values.phone,
        email: created.email ?? null,
        gender: created.gender ?? values.gender,
      } as Patient;
      setPatients((prev) => [newPatient, ...prev]);
      setSelectedPatient(newPatient);
      createPatientForm.resetFields();
      setShowCreatePatient(false);
      message.success("Patient created");
    } catch (err: any) {
      if (err?.errorFields) return; // validation error, form already shows it
      message.error(err?.response?.data?.message ?? "Failed to create patient");
    } finally {
      setCreatePatientLoading(false);
    }
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      message.warning("Cart is empty. Please add items to cart.");
      return;
    }
    if (!selectedPatient?.id) {
      message.warning("Please select a patient before checkout.");
      return;
    }
    setIsPaymentModalOpen(true);
  };

  const handlePaymentSave = async (paymentData: PaymentData) => {
    if (!selectedPatient?.id) {
      message.warning("Please select a patient");
      return;
    }

    if (cart.length === 0) {
      message.warning("Cart is empty. Please add items to cart.");
      return;
    }

    const subtotal = calculateSubtotal();
    const totalDiscount = calculateTotalDiscount();
    const netAmount = subtotal - totalDiscount;
    const totalPaid =
      (paymentData.upi || 0) +
      (paymentData.cash || 0) +
      (paymentData.card || 0) +
      (paymentData.cheque || 0) +
      (paymentData.online || 0);

    // if (totalPaid < netAmount) {
    //   message.warning(
    //     `Payment amount (₹${totalPaid}) is less than total (₹${netAmount})`
    //   );
    //   return;
    // }

    // Convert cart items to backend format; for batch-tracked items use batchId or resolve nearest-expiry batch
    const saleItems = cart.map((item) => {
      const pharmacyItem = pharmacyItems.find((pi) => pi.id === item.pharmacyItemId);
      let batchIdForPayload: number | null = item.batchId ?? null;
      if (pharmacyItem?.is_batch_tracked && batchIdForPayload == null) {
        const nearestBatch = getBatchWithNearestExpiry(pharmacyItem);
        batchIdForPayload = nearestBatch?.id ?? null;
      }
      if (pharmacyItem?.is_batch_tracked && batchIdForPayload == null) {
        throw new Error(`Batch is required for batch-tracked item: ${item.name}. No usable batch found.`);
      }
      const itemMrp = item.mrp ?? item.unitPrice * (1 + (item.taxPercentage || 0) / 100);
      const gross = item.quantity * itemMrp;
      const itemDiscountAmount = gross * ((item.discountPercentage || 0) / 100);
      const grossAfterDiscount = Math.max(0, gross - itemDiscountAmount);
      const taxPct = item.taxPercentage || 0;
      const itemTaxableAmount = taxPct ? (grossAfterDiscount / (1 + taxPct / 100)) : grossAfterDiscount;
      const itemTaxAmount = grossAfterDiscount - itemTaxableAmount;
      const itemNetAmount = grossAfterDiscount;
      const payload: Record<string, unknown> = {
        pharmacy_item_id: item.pharmacyItemId,
        pharmacy_batch_id: batchIdForPayload,
        quantity: item.quantity,
        mrp: itemMrp,
        selling_price: item.unitPrice,
        discount_percentage: item.discountPercentage || 0,
        discount_amount: Number(itemDiscountAmount.toFixed(2)),
        tax_percentage: item.taxPercentage || 0,
        tax_amount: Number(itemTaxAmount.toFixed(2)),
        net_amount: Number(itemNetAmount.toFixed(2)),
      };
      if (isEditMode && item.saleItemId) {
        payload.id = item.saleItemId;
      }
      return payload;
    });

    // Prepare sale data matching backend API structure
    // Backend expects: patient_id, visit_id, doctor_id, sale_date, discount_percentage (order-level only), status, delivery_status, notes, items
    const saleData = {
      patient_id: selectedPatient?.id || null,
      visit_id: isEditMode ? loadedVisitId : null,
      doctor_id: selectedUserId || null,
      sale_date: saleDate.toISOString().split("T")[0],
      discount_percentage: discountPercentage || 0, // Order-level discount percentage (0-100)
      status:
        totalPaid === 0
          ? "pending"
          : totalPaid >= netAmount
          ? "paid"
          : "partially_paid",
      delivery_status: deliveryStatus || "completed",
      notes: notes || paymentData.paymentNote || null,
      items: saleItems,
    };

    try {
      let resolvedSaleId: number;

      if (isEditMode && saleId) {
        // Update existing sale
        const id = parseInt(saleId, 10);
        await updatePOSSale(id, saleData as any);
        resolvedSaleId = id;
      } else {
        // Create new pharmacy sale
        const response: any = await createPOSSale(saleData);
        resolvedSaleId = response?.data?.id || response?.id;
        if (!resolvedSaleId) {
          throw new Error("Sale ID not returned from API");
        }
      }

      // Create payment records using new format (skip if edit and no new payments - optional)
      const { processPayment } = await import("../../apis/payment");

      // Build payments array with all payment modes
      const payments: any[] = [];
      const baseTimestamp = Date.now();

      if (paymentData.cash > 0) {
        payments.push({
          amount: paymentData.cash,
          payment_mode: "cash",
          payment_date: saleDate.toISOString().split("T")[0],
          transaction_id: `TXN-${baseTimestamp}-CASH`,
          notes: paymentData.paymentNote || null,
          allocation_type: "manual",
          allocation_notes: paymentData.paymentNote || null,
        });
      }

      if (paymentData.upi > 0) {
        payments.push({
          amount: paymentData.upi,
          payment_mode: "upi",
          payment_date: saleDate.toISOString().split("T")[0],
          transaction_id: `TXN-${baseTimestamp}-UPI`,
          notes: paymentData.paymentNote || null,
          allocation_type: "manual",
          allocation_notes: paymentData.paymentNote || null,
        });
      }

      if (paymentData.card > 0) {
        payments.push({
          amount: paymentData.card,
          payment_mode: "card",
          payment_date: saleDate.toISOString().split("T")[0],
          transaction_id: `TXN-${baseTimestamp}-CARD`,
          notes: paymentData.paymentNote || null,
          allocation_type: "manual",
          allocation_notes: paymentData.paymentNote || null,
        });
      }

      if (paymentData.cheque > 0) {
        payments.push({
          amount: paymentData.cheque,
          payment_mode: "cheque",
          payment_date: saleDate.toISOString().split("T")[0],
          transaction_id: `TXN-${baseTimestamp}-CHEQUE`,
          notes: paymentData.paymentNote || null,
          allocation_type: "manual",
          allocation_notes: paymentData.paymentNote || null,
        });
      }

      if (paymentData.online > 0) {
        payments.push({
          amount: paymentData.online,
          payment_mode: "online",
          payment_date: saleDate.toISOString().split("T")[0],
          transaction_id: `TXN-${baseTimestamp}-ONLINE`,
          notes: paymentData.paymentNote || null,
          allocation_type: "manual",
          allocation_notes: paymentData.paymentNote || null,
        });
      }

      // Process all payments in a single API call using new format
      if (payments.length > 0) {
        await processPayment({
          source_type: "pharmacy_sale",
          source_ids: [resolvedSaleId],
          payments: payments,
          source_amounts: {
            [resolvedSaleId]: netAmount,
          },
          invoice_date: saleDate.toISOString().split("T")[0],
        });
      }

      showNotification(
        "success",
        isEditMode ? "Sale updated successfully!" : "Sale completed successfully!",
        "",
        "topRight"
      );

      // Reset cart and form
      setCart([]);
      setSelectedPatient(null);
      setSelectedUserId(null);
      setDiscount(0);
      setDiscountPercentage(0);
      setNotes("");
      setSaleDate(new Date());
      setIsPaymentModalOpen(false);
      setLoadedVisitId(null);
      setIsEditMode(false);

      if (isEditMode) {
        navigate("/pharmacy/pos/list");
      } else {
        // Open backend-generated pharmacy bill in a new tab for printing
        try {
          const htmlRes: any = await getPharmacyBillHtml({ pharmacySaleId: resolvedSaleId });
          const blobUrl = URL.createObjectURL(new Blob([htmlRes.data], { type: "text/html" }));
          window.open(blobUrl, "_blank");
          setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        } catch (printErr) {
          console.warn("Could not open print receipt:", printErr);
        }
      }
    } catch (error: any) {
      console.error("Error creating sale:", error);
      showNotification(
        "error",
        error?.response?.data?.error?.details ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to complete sale",
        "",
        "topRight"
      );
    }
  };

  const handlePrintReceipt = () => {
    // This will be called from PaymentModal's print button
    // For now, we'll handle it after save
  };

  const printReceipt = (saleData: any) => {
    // Simple receipt printing (can be enhanced with a proper receipt component)
    const receiptWindow = window.open("", "_blank");
    if (receiptWindow) {
      const totalPaid =
        saleData.payments?.reduce(
          (sum: number, payment: any) => sum + (payment.amount || 0),
          0
        ) || 0;
      const total = saleData.total || calculateTotal();
      const receiptContent = `
        <html>
          <head>
            <title>Receipt</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              .header { text-align: center; margin-bottom: 20px; }
              .item { margin: 10px 0; }
              .total { font-weight: bold; margin-top: 20px; }
              .footer { margin-top: 30px; text-align: center; font-size: 12px; }
            </style>
          </head>
          <body>
            <div class="header">
              <h2>Pharmacy Receipt</h2>
              <p>Sale #${saleData.saleNumber || saleData.id}</p>
              <p>Date: ${format(
        new Date(saleData.saleDate || new Date()),
        "dd MMM yyyy, HH:mm"
      )}</p>
            </div>
            ${selectedPatient
          ? `<p><strong>Patient:</strong> ${selectedPatient.firstName} ${selectedPatient.lastName}</p>`
          : ""
        }
            <hr>
            ${(saleData.items || cart)
          .map(
            (item: any) => `
              <div class="item">
                <p><strong>${item.name}</strong> - ${item.quantity} x ₹${item.unitPrice} = ₹${item.total}</p>
              </div>
            `
          )
          .join("")}
            <hr>
            <div class="total">
              <p>Subtotal: ₹${saleData.subtotal || calculateSubtotal()}</p>
              ${(saleData.discount || discount) > 0
          ? `<p>Discount: -₹${saleData.discount || discount}</p>`
          : ""
        }
              <p>Total: ₹${total}</p>
              <p>Paid: ₹${totalPaid}</p>
              ${totalPaid > total ? `<p>Change: ₹${totalPaid - total}</p>` : ""}
            </div>
            <div class="footer">
              <p>Thank you for your purchase!</p>
            </div>
          </body>
        </html>
      `;
      receiptWindow.document.write(receiptContent);
      receiptWindow.document.close();
      receiptWindow.print();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && filteredItems.length > 0) {
      addToCart(filteredItems[0]);
    }
  };

  if (editLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gray-50">
        <p className="text-gray-600">Loading sale...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-2 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex justify-between items-center">
          <h1 className="text-lg font-bold text-gray-900">
            {isEditMode ? "Edit Sale" : "Pharmacy POS"}
          </h1>
          {isEditMode && (
            <Button
              type="default"
              onClick={() => navigate("/pharmacy/pos/list")}
            >
              Back to Sales List
            </Button>
          )}
        </div>
      </div>

      <div className="flex overflow-hidden flex-col flex-1 p-2 min-h-0">
        {/* Row 1: Sale date, Select patient, Select user — one line */}
        <div className="flex-shrink-0 p-3 mb-2 bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Sale Date
              </label>
              <DatePicker
                selected={saleDate}
                onChange={(date: Date | null) =>
                  setSaleDate(date || new Date())
                }
                dateFormat="dd MMM yyyy"
                className="px-3 w-full h-8 text-sm rounded-md border border-gray-300 focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Select Patient
              </label>
              <Select
                showSearch
                size="small"
                style={{ width: "100%", height: "30px" }}
                placeholder="Search and select patient..."
                value={selectedPatient?.id || undefined}
                onChange={(value) => {
                  const patient = patients.find((p) => p.id === value);
                  setSelectedPatient(patient || null);
                }}
                filterOption={(input, option: any) => {
                  const patient = patients.find((p) => p.id === option?.value);
                  if (!patient) return false;
                  const searchText = input.toLowerCase();
                  const fullName = `${patient.firstName || ""} ${patient.lastName || ""
                    }`.toLowerCase();
                  return (
                    fullName.includes(searchText) ||
                    (!!patient.phone &&
                      patient.phone.toLowerCase().includes(searchText)) ||
                    (!!patient.email &&
                      patient.email.toLowerCase().includes(searchText))
                  );
                }}
                allowClear
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <div className="border-t border-gray-200 p-1">
                      <Button
                        type="link"
                        size="small"
                        icon={<PlusOutlined />}
                        className="w-full text-left"
                        onClick={() => setShowCreatePatient(true)}
                      >
                        Create new patient
                      </Button>
                    </div>
                  </>
                )}
              >
                {patients.map((patient) => (
                  <Option key={patient.id} value={patient.id}>
                    {patient.firstName} {patient.lastName}{" "}
                    {patient.phone && `- ${patient.phone}`}
                  </Option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Select User
              </label>
              <Select
                showSearch
                size="small"
                style={{ width: "100%", height: "30px" }}
                placeholder="Search and select user..."
                value={selectedUserId || undefined}
                onChange={(value) => setSelectedUserId(value)}
                filterOption={(input, option: any) => {
                  const user = users.find((u) => u.id === option?.value);
                  if (!user) return false;
                  const searchText = input.toLowerCase();
                  const fullName = `${user.firstName || ""} ${user.lastName || ""
                    }`.toLowerCase();
                  return (
                    fullName.includes(searchText) ||
                    (!!user.phone &&
                      user.phone.toLowerCase().includes(searchText)) ||
                    (!!user.email &&
                      user.email.toLowerCase().includes(searchText)) ||
                    (!!user.registrationNumber &&
                      user.registrationNumber
                        .toLowerCase()
                        .includes(searchText))
                  );
                }}
                allowClear
              >
                {users.map((user) => (
                  <Option key={user.id} value={user.id}>
                    {user.firstName} {user.lastName}{" "}
                    {user.phone && `- ${user.phone}`}
                  </Option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Delivery Status
              </label>
              <Select
                size="small"
                style={{ width: "100%", height: "30px" }}
                value={deliveryStatus}
                onChange={(value) =>
                  setDeliveryStatus(
                    value as "pending" | "dispatched" | "received" | "completed"
                  )
                }
              >
                <Option value="pending">Pending</Option>
                <Option value="dispatched">Dispatched</Option>
                <Option value="received">Received</Option>
                <Option value="completed">Completed</Option>
              </Select>
            </div>
          </div>
        </div>

        {/* Row 2: Cart section — two columns: cart list (large) | summary + notes */}
        <div className="flex flex-1 gap-4 min-h-0">
          {/* Left: Add items strip + Cart list (large scrollable area) */}
          <div className="flex overflow-hidden flex-col flex-1 min-w-0 min-h-0 bg-white rounded-lg border border-gray-200 shadow-sm">
            {/* Compact add-items row — single line */}
            <div className="flex-shrink-0 px-2 py-2 border-b border-gray-200 bg-gray-50/80">
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[180px]">
                  <label className="block mb-1 text-sm font-medium text-gray-600">
                    Add Bundle
                  </label>
                  <Select
                    size="small"
                    style={{ width: "100%" }}
                    placeholder="Select bundle..."
                    value={selectedBundleId ?? undefined}
                    onChange={(value) => setSelectedBundleId(value ?? null)}
                    allowClear
                    onClear={() => {
                      setSelectedBundleId(null);
                      setBundleDetail(null);
                    }}
                  >
                    {bundles
                      .filter((b) => b.isActive ?? b.is_active ?? true)
                      .map((b) => (
                        <Option key={b.id} value={b.id}>
                          {b.name} ({b.code})
                        </Option>
                      ))}
                  </Select>
                </div>
                <div className="flex-1 min-w-[180px]">
                  <label className="block mb-1 text-sm font-medium text-gray-600">
                    Add Item
                  </label>
                  <Select
                    key={itemSelectKey}
                    showSearch
                    size="small"
                    style={{ width: "100%" }}
                    placeholder="Search pharmacy item..."
                    value={undefined}
                    onChange={(value) => {
                      const selectedItem = pharmacyItems.find(
                        (pi) => pi.id === value
                      );
                      if (selectedItem) {
                        handleItemSelect(selectedItem);
                        setItemSelectKey((k) => k + 1);
                      }
                    }}
                    filterOption={(input, option: any) => {
                      const selectedItem = pharmacyItems.find(
                        (pi) => pi.id === option?.value
                      );
                      if (!selectedItem) return false;
                      const searchText = input.toLowerCase();
                      return (
                        selectedItem.name.toLowerCase().includes(searchText) ||
                        selectedItem.sku.toLowerCase().includes(searchText) ||
                        (!!selectedItem.barcode &&
                          selectedItem.barcode.toLowerCase().includes(searchText)) ||
                        (!!selectedItem.molecule &&
                          selectedItem.molecule.toLowerCase().includes(searchText))
                      );
                    }}
                  >
                    {pharmacyItems.map((pharmacyItem) => (
                      <Option
                        key={pharmacyItem.id}
                        value={pharmacyItem.id}
                        disabled={!hasUsableBatches(pharmacyItem)}
                      >
                        <span className="flex flex-wrap gap-2 items-center text-sm">
                          <span>
                            {pharmacyItem.name} - {pharmacyItem.sku}
                            {pharmacyItem.form && ` (${pharmacyItem.form})`}
                          </span>
                          {!hasUsableBatches(pharmacyItem) && (
                            <Tag color="red">Not available</Tag>
                          )}
                        </span>
                      </Option>
                    ))}
                  </Select>
                </div>
              </div>
              {bundleDetailLoading && selectedBundleId && (
                <span className="block mt-1 text-xs text-gray-500">Adding bundle to cart...</span>
              )}
            </div>

            {/* Cart list: large scrollable area — min-height ensures comfortable space */}
            <div className="flex-1 min-h-[380px] overflow-y-auto flex flex-col">
              <div className="flex-shrink-0 px-2 pt-2 pb-1 border-b border-gray-100">
                <h2 className="flex gap-2 items-center text-sm font-semibold text-gray-800">
                  <ShoppingCartOutlined />
                  Cart ({cart.length})
                </h2>
              </div>
              <div className="overflow-y-auto flex-1 p-2 min-h-0">
                {cart.length === 0 ? (
                  <div className="flex flex-col justify-center items-center px-2 py-8 text-gray-500">
                    <ShoppingCartOutlined className="mb-1 text-3xl text-gray-300" />
                    <p className="text-sm">Cart is empty.</p>
                    <p className="text-xs mt-0.5">Use the fields above to add a bundle or item.</p>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {cart.map((item: any) => {
                      const itemData = pharmacyItems.find(
                        (pi) => pi.id === item.pharmacyItemId
                      );
                      const taxPct = item.taxPercentage || 0;
                      const itemMrp = item.mrp ?? item.unitPrice * (1 + taxPct / 100);
                      const itemGross = item.quantity * itemMrp;
                      const itemDiscountAmount = itemGross * ((item.discountPercentage || 0) / 100);
                      const itemGrossAfterDiscount = Math.max(0, itemGross - itemDiscountAmount);
                      const itemTaxableValue = taxPct
                        ? itemGrossAfterDiscount / (1 + taxPct / 100)
                        : itemGrossAfterDiscount;
                      const itemTaxAmount = itemGrossAfterDiscount - itemTaxableValue;
                      return (
                        <div
                          key={item.id}
                          className="bg-white border border-gray-200 rounded py-1.5 px-2 shadow-sm flex items-center gap-2 flex-nowrap"
                        >
                          <div className="flex overflow-hidden flex-col flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-nowrap overflow-hidden">
                              <span className="text-sm font-medium text-gray-900 truncate">{item.name}</span>
                              {item.batchNumber && (
                                <Tag
                                  color="blue"
                                  className="flex-shrink-0 text-xs cursor-pointer"
                                  onClick={() => {
                                    if (itemData) {
                                      setSelectedItemForBatch(itemData);
                                      setSelectedCartItemForBatch(item.id);
                                      fetchBatchesForItem(itemData);
                                      setIsBatchModalOpen(true);
                                    }
                                  }}
                                >
                                  {item.batchNumber}
                                </Tag>
                              )}
                            </div>
                            <div className="text-gray-500 text-xs flex-shrink-0 mt-0.5">
                              {item.sku && <span>• {item.sku}</span>}
                              {item.form && <span> • {item.form}</span>}
                              {item.strength && <span> • {item.strength}</span>}
                              {(item.mrp ?? 0) > 0 && (
                                <span> • MRP ₹{Number(item.mrp).toFixed(2)}</span>
                              )}
                              {(item.taxPercentage ?? 0) > 0 && (
                                <span> • Tax {item.taxPercentage}%</span>
                              )}

                            </div>
                          </div>
                          <div className="flex flex-shrink-0 gap-1 items-center">
                            <Button
                              size="small"
                              type="text"
                              className="!p-0.5 !min-w-0"
                              icon={<MinusOutlined />}
                              onClick={() => updateCartItemQuantity(item.id, -1)}
                            />
                            <Input
                              type="number"
                              min={1}
                              value={item.quantity}
                              onChange={(e) => {
                                const qty = parseInt(e.target.value) || 1;
                                setCart(
                                  cart.map((cartItem) => {
                                    if (cartItem.id === item.id) {
                                      const updatedItem = {
                                        ...cartItem,
                                        quantity: qty,
                                      };
                                      return {
                                        ...updatedItem,
                                        total: calculateItemTotal(updatedItem),
                                      };
                                    }
                                    return cartItem;
                                  })
                                );
                              }}
                              className="!w-12 !h-7 text-center text-xs rounded"
                            />
                            <Button
                              size="small"
                              type="text"
                              className="!p-0.5 !min-w-0"
                              icon={<PlusOutlined />}
                              onClick={() => updateCartItemQuantity(item.id, 1)}
                            />
                          </div>
                          <div className="flex flex-wrap flex-shrink-0 gap-1 items-center">
                            <span className="text-xs text-gray-500">Disc</span>
                            <Input
                              type="number"
                              min={0}
                              max={100}
                              step={0.01}
                              value={item.discountPercentage ?? 0}
                              onChange={(e) =>
                                updateCartItemDiscountPercentage(
                                  item.id,
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              className="w-16 !h-7 text-xs text-center"
                              suffix="%"
                              title="Discount %"
                            />
                            <div className="text-[11px] text-gray-600 whitespace-nowrap">
                              <span>Disc ₹{itemDiscountAmount.toFixed(2)}</span>
                              <span> • Taxable ₹{itemTaxableValue.toFixed(2)}</span>
                              <span> • GST ₹{itemTaxAmount.toFixed(2)}</span>
                            </div>
                          </div>
                          <div className="flex-shrink-0 w-16 text-right">
                            <span className="text-sm font-semibold text-gray-900">₹{(item.total).toFixed(2)}</span>
                          </div>
                          <Button
                            type="text"
                            danger
                            size="small"
                            className="!p-0.5 !min-w-0 flex-shrink-0"
                            icon={<DeleteOutlined />}
                            onClick={() => removeFromCart(item.id)}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right column: Summary + Notes — fixed width, always visible */}
          <div className="w-full sm:w-[360px] flex-shrink-0 flex flex-col gap-2 min-h-0 overflow-y-auto">
            <div className="flex-shrink-0 p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Notes
              </label>
              <Input.TextArea
                size="middle"
                placeholder="Add notes for this sale..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="resize-none"
              />
            </div>
            <div className="flex-shrink-0 p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
              <h3 className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-800">
                <ShoppingCartOutlined />
                Order Summary
              </h3>
              <div className="mb-4 space-y-2">
                <div className="flex justify-between text-sm text-gray-700">
                  <span>Subtotal</span>
                  <span>₹{calculateSubtotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-700">
                  <span>Tax</span>
                  <span>₹{calculateTotalTax().toFixed(2)}</span>
                </div>
                {/* <div className="flex gap-2 justify-between items-center text-sm">
                  <span className="text-gray-700">Discount %</span>
                  <Input
                    type="number"
                    suffix="%"
                    value={discountPercentage}
                    onChange={(e) => {
                      const percentage = parseFloat(e.target.value) || 0;
                      setDiscountPercentage(
                        Math.min(100, Math.max(0, percentage))
                      );
                      const subtotal = calculateSubtotal();
                      setDiscount(subtotal * (percentage / 100));
                    }}
                    className="w-20"
                    placeholder="0"
                    min={0}
                    max={100}
                  />
                </div>
                <div className="flex justify-between text-sm text-gray-700">
                  <span>Discount</span>
                  <span>₹{calculateTotalDiscount().toFixed(2)}</span>
                </div> */}
                <div className="flex justify-between pt-2 text-base font-bold text-gray-900 border-t border-gray-200">
                  <span>Total</span>
                  <span>₹{calculateTotal().toFixed(2)}</span>
                </div>
              </div>
              <Button
                type="primary"
                size="large"
                block
                onClick={handleCheckout}
                disabled={cart.length === 0}
                className="bg-primary hover:bg-hover"
              >
                Checkout
              </Button>
            </div>

          </div>
        </div>
      </div>

      {/* Batch Selection Modal */}
      {isBatchModalOpen && (
        <div
          className="overflow-y-auto fixed inset-0 z-50"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex justify-center items-center px-4 pt-4 pb-20 min-h-screen text-center sm:block sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
              onClick={() => {
                setIsBatchModalOpen(false);
                setSelectedItemForBatch(null);
                setSelectedCartItemForBatch(null);
                setSelectedBatch(null);
                setAvailableBatches([]);
                setQuantityToAdd(1);
              }}
            ></div>

            {/* Modal */}
            <div className="inline-block overflow-hidden text-left align-bottom bg-white rounded-lg shadow-xl transition-all transform sm:align-middle sm:max-w-2xl sm:w-full">
              {/* Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">
                  Select Batch - {selectedItemForBatch?.name}
                  {quantityToAdd > 1 && (
                    <span className="ml-2 text-base font-normal text-gray-600">
                      (Qty: {quantityToAdd})
                    </span>
                  )}
                </h2>
                <button
                  onClick={() => {
                    setIsBatchModalOpen(false);
                    setSelectedItemForBatch(null);
                    setSelectedCartItemForBatch(null);
                    setSelectedBatch(null);
                    setAvailableBatches([]);
                    setQuantityToAdd(1);
                  }}
                  className="text-gray-400 transition-colors hover:text-gray-600"
                >
                  <CloseOutlined className="text-xl" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-4">
                {isLoadingBatches ? (
                  <div className="py-8 text-center">Loading batches...</div>
                ) : availableBatches.length === 0 ? (
                  <div className="space-y-4">
                    <div className="py-4 text-center text-gray-500">
                      No batches available for this item. You can still add it
                      without a batch.
                    </div>
                    <Button
                      type="primary"
                      block
                      onClick={() => {
                        setSelectedBatch(null);
                        addToCartWithBatch();
                      }}
                      className="bg-primary hover:bg-hover"
                    >
                      Add Without Batch
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-y-auto max-h-96">
                    <div className="space-y-3">
                      {availableBatches.map((batch: PharmacyBatch) => {
                        const expiryDate = batch.expiry_date;
                        const mfgDate = batch.manufacturing_date;
                        const availableQty = batch.available_quantity || 0;
                        const sellingPrice = parseFloat(
                          batch.selling_price || "0"
                        );
                        const mrp = batch.mrp || sellingPrice;
                        const isExpired =
                          expiryDate && new Date(expiryDate) < new Date();
                        const isLowStock = availableQty <= 10;

                        return (
                          <div
                            key={batch.id}
                            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${selectedBatch?.id === batch.id
                                ? "border-primary bg-blue-50"
                                : "border-gray-200 hover:border-gray-300"
                              } ${isExpired ? "bg-red-50 border-red-300" : ""}`}
                            onClick={() => setSelectedBatch(batch)}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex gap-2 items-center mb-2">
                                  <h4 className="font-semibold text-gray-900">
                                    Batch: {batch.batch_number}
                                  </h4>
                                  {isExpired && <Tag color="red">Expired</Tag>}
                                  {isLowStock && !isExpired && (
                                    <Tag color="orange">Low Stock</Tag>
                                  )}
                                </div>
                                <div className="grid grid-cols-2 gap-3 mt-2 text-sm">
                                  <div>
                                    <span className="text-gray-600">
                                      Available Qty:
                                    </span>
                                    <span className="ml-2 font-medium text-gray-900">
                                      {availableQty}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">MRP:</span>
                                    <span className="ml-2 font-medium text-gray-900">
                                      ₹{mrp.toFixed(2)}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">
                                      Selling Price:
                                    </span>
                                    <span className="ml-2 font-medium text-primary">
                                      ₹{sellingPrice.toFixed(2)}
                                    </span>
                                  </div>
                                  {expiryDate && (
                                    <div>
                                      <span className="text-gray-600">
                                        Expiry Date:
                                      </span>
                                      <span
                                        className={`ml-2 font-medium ${isExpired
                                            ? "text-red-600"
                                            : "text-gray-900"
                                          }`}
                                      >
                                        {format(
                                          new Date(expiryDate),
                                          "dd MMM yyyy"
                                        )}
                                      </span>
                                    </div>
                                  )}
                                  {mfgDate && (
                                    <div>
                                      <span className="text-gray-600">
                                        Mfg Date:
                                      </span>
                                      <span className="ml-2 font-medium text-gray-900">
                                        {format(
                                          new Date(mfgDate),
                                          "dd MMM yyyy"
                                        )}
                                      </span>
                                    </div>
                                  )}
                                  {batch.purchase_price && (
                                    <div>
                                      <span className="text-gray-600">
                                        Purchase Price:
                                      </span>
                                      <span className="ml-2 font-medium text-gray-900">
                                        ₹
                                        {parseFloat(
                                          batch.purchase_price || "0"
                                        ).toFixed(2)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="ml-4">
                                {selectedBatch?.id === batch.id && (
                                  <div className="flex justify-center items-center w-6 h-6 rounded-full bg-primary">
                                    <span className="text-xs text-white">
                                      ✓
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 justify-end px-6 py-4 border-t border-gray-200">
                <Button
                  onClick={() => {
                    setIsBatchModalOpen(false);
                    setSelectedItemForBatch(null);
                    setSelectedCartItemForBatch(null);
                    setSelectedBatch(null);
                    setAvailableBatches([]);
                    setQuantityToAdd(1);
                  }}
                  className="px-6 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={addToCartWithBatch}
                  disabled={!selectedBatch && availableBatches.length > 0}
                  className="px-6 py-2 text-white rounded-md bg-primary hover:bg-hover disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {selectedCartItemForBatch ? "Update Batch" : "Add to Cart"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Patient Modal */}
      <Modal
        title="Create New Patient"
        open={showCreatePatient}
        onCancel={() => {
          setShowCreatePatient(false);
          createPatientForm.resetFields();
        }}
        onOk={handleCreatePatient}
        okText="Create Patient"
        confirmLoading={createPatientLoading}
        width={420}
        destroyOnClose
      >
        <Form form={createPatientForm} layout="vertical" className="mt-4">
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[{ required: true, message: "Required" }]}
            >
              <Input placeholder="First name" />
            </Form.Item>
            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[{ required: true, message: "Required" }]}
            >
              <Input placeholder="Last name" />
            </Form.Item>
          </div>
          <Form.Item
            label="Phone"
            name="phone"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input placeholder="Phone number" />
          </Form.Item>
          <Form.Item
            label="Gender"
            name="gender"
            rules={[{ required: true, message: "Required" }]}
          >
            <Radio.Group>
              <Radio value="male">Male</Radio>
              <Radio value="female">Female</Radio>
              <Radio value="other">Other</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        total={calculateTotal()}
        discount={0}
        sourceType="pharmacy"
        allowPartialOrUnpaid
        sourceId={selectedUserId ?? 0}
        patientId={selectedPatient?.id}
        patientName={
          selectedPatient
            ? `${selectedPatient.firstName} ${selectedPatient.lastName}`
            : "Counter Sale"
        }
        onSave={handlePaymentSave}
      // onPrint={handlePrintReceipt}
      />
    </div>
  );
};

export default PharmacyPOS;
